import { useCallback, useEffect, useState } from 'react'

import { selectLastRecurrenceSuccess } from '@/features/domain/history'
import { selectStorageSpaceData } from '@/features/domain/order'
import { checkRestoreRecurrentOrders } from '@/features/domain/order/actions'
import { selectRecurrences } from '@/features/domain/routeplan'
import { useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'
import { useSelector } from 'react-redux'
import { usePlanningRange } from './usePlanningRange'

export function useCheckEligibleRestoreRecurrentOrders() {
  const { currentOrders, totalOrders } = useSelector(selectStorageSpaceData)
  const lastRecurrenceSuccess = useSelector(selectLastRecurrenceSuccess)
  const recurrences = useSelector(selectRecurrences)
  const { planningRange } = usePlanningRange()
  const dispatch = useAppDispatch()
  const toast = useNotification()

  const [restorable, setRestorable] = useState<boolean>(true)
  const [loading, setLoading] = useState(false)

  const eligibleRestoreRecurrentOrders = useCallback(async () => {
    // Don't make request if no recurrences exist
    const hasRecurrences = Object.keys(recurrences).length > 0
    if (!hasRecurrences) {
      setRestorable(false)
      return
    }

    setLoading(true)
    try {
      const result = await dispatch(checkRestoreRecurrentOrders())

      if (!checkRestoreRecurrentOrders.fulfilled.match(result)) {
        throw new Error(result.payload?.message ?? 'Internal error')
      }

      const hasEligibleDates = Object.keys(result.payload).length > 0
      setRestorable(hasEligibleDates)
    } catch (error) {
      setRestorable(false)
      toast.error(error?.message)
      console.error('Failed to check restore recurrent orders:', error)
    } finally {
      setLoading(false)
    }
  }, [dispatch, toast, recurrences])

  useEffect(() => {
    eligibleRestoreRecurrentOrders()
    // planningRange, lastRecurrenceSuccess, currentOrders and totalOrders are included to re-check when storage changes
  }, [
    eligibleRestoreRecurrentOrders,
    lastRecurrenceSuccess,
    currentOrders,
    planningRange,
    totalOrders,
  ])

  return {
    eligibleRestoreRecurrentOrders,
    loading,
    restorable,
  }
}
